import type { Map } from 'immutable'
import type {
  SettingsFormState,
  SettingsFormAction,
  UserProfile,
} from '../../types'
import { selectUserData } from '../profile/redux/state-types'
import type { ProfileSettingsInitialValues } from './settings-dirty'

export const FORM_INIT = 'FORM_INIT'
export const FORM_FIELD = 'FORM_FIELD'

type S = SettingsFormState
type A = SettingsFormAction

export function formReducer (state: S, action: A): S {
  switch (action.type) {
    case FORM_INIT:
      return { ...state, ...action.payload }
    case FORM_FIELD:
      return { ...state, [action.field]: action.value }
    default:
      return state
  }
}

export const FORM_INITIAL_STATE: SettingsFormState = {
  displayName: '',
  bio: '',
  locationValue: '',
  birthdayValue: '',
  selectedTagIds: [],
  profileVisibility: 'private',
  locationVisibility: 'private',
  birthdayVisibility: 'private',
  memberSinceVisibility: 'public',
  bioVisibility: 'public',
  topicsVisibility: 'public',
  archetypesVisibility: 'private',
  journeyStageVisibility: 'private',
  selectedArchetypes: [],
  journeyStage: null,
}

// ── buildInitialFormState ───────────────────────────────────────────
// Pure function that derives both the form state and the dirty-detection
// baseline from Apollo profile data + Redux user data + canonical center
// tag options. Called synchronously from the form's useReducer initializer
// so the form paints populated on render 1 when data is cached.
//
// Ported verbatim from the previous shadow-copy useEffect in
// CommunityProfileSettingsPage.tsx (pre-split). Two branches:
//   1. New user — profile query resolved but returned null → defaults +
//      displayName from Redux user.
//   2. Existing user — profile present → derive every field from the
//      profile; match topics against availableTopicsJS, falling back to
//      the profile's raw contentIds when the canonical centers query
//      hasn't loaded anything usable.

interface AvailableTopic {
  id: number
  label: string
}

export interface BuildInitialFormStateArgs {
  profile: UserProfile | null | undefined
  /** true once GET_CURRENT_PROFILE has resolved (even if the payload is null for a new user) */
  profileDataPresent: boolean
  user: Map<string, unknown> | undefined
  availableTopicsJS: AvailableTopic[]
  /**
   * true once GET_CANONICAL_CENTERS has loaded or errored — don't derive
   * topic matches before this
   */
  tagOptionsReady: boolean
}

export interface BuildInitialFormStateResult {
  formState: SettingsFormState
  initialValues: ProfileSettingsInitialValues
  /**
   * false only while we have a profile but tag options haven't settled —
   * the container gate should prevent this case in practice, but the
   * helper stays defensive so tests can exercise it.
   */
  ready: boolean
}

function readReduxUsername (user: Map<string, unknown> | undefined): string {
  if (!user) return ''
  const userMap = selectUserData({ user })
  if (!userMap) return ''
  const username = userMap.getIn(['data', 'username'])
  return typeof username === 'string' ? username : ''
}

function validVis (v: string | null | undefined, fallback: string): string {
  return (v === 'public' || v === 'private') ? v : fallback
}

function newUserResult (user: Map<string, unknown> | undefined): BuildInitialFormStateResult {
  const displayName = readReduxUsername(user)
  const formState: SettingsFormState = {
    ...FORM_INITIAL_STATE,
    displayName,
  }
  const initialValues: ProfileSettingsInitialValues = {
    displayName,
    bio: '',
    location: '',
    birthday: '',
    profileVisibility: FORM_INITIAL_STATE.profileVisibility,
    locationVisibility: FORM_INITIAL_STATE.locationVisibility,
    birthdayVisibility: FORM_INITIAL_STATE.birthdayVisibility,
    memberSinceVisibility: FORM_INITIAL_STATE.memberSinceVisibility,
    bioVisibility: FORM_INITIAL_STATE.bioVisibility,
    topicsVisibility: FORM_INITIAL_STATE.topicsVisibility,
    archetypesVisibility: FORM_INITIAL_STATE.archetypesVisibility,
    journeyStageVisibility: FORM_INITIAL_STATE.journeyStageVisibility,
    centerContentIds: '',
    archetype: '',
    journeyStage: '',
  }
  return { formState, initialValues, ready: true }
}

function existingUserResult (
  profile: UserProfile,
  availableTopicsJS: AvailableTopic[],
): BuildInitialFormStateResult {
  const initDisplayName = profile.displayName || ''
  const initBio = profile.bio || ''
  const initLocation = profile.location || ''
  const initBirthday = profile.birthday || ''
  const initProfileVis = validVis(profile.profileVisibility, 'private')
  const initLocationVis = validVis(profile.locationVisibility, 'private')
  const initBirthdayVis = validVis(profile.birthdayVisibility, 'private')
  const initMemberSinceVis = validVis(profile.memberSinceVisibility, 'public')
  const initBioVis = validVis(profile.bioVisibility, 'public')
  const initTopicsVis = validVis(profile.topicsVisibility, 'public')
  const initArchetypesVis = validVis(profile.archetypeVisibility, 'private')
  const initJourneyStageVis = validVis(profile.yourJourneyVisibility, 'private')
  const initArchetypes = profile.archetype
    ? profile.archetype.split(',').map((s) => s.trim()).filter(Boolean)
    : []
  const initJourney = profile.yourJourney || null

  const topics = profile.topics || []
  const initTagIds: number[] = []
  if (topics.length > 0) {
    if (availableTopicsJS.length > 0) {
      // Match profile topics against the full available-options list so
      // chip labels come from canonical centers.
      const topicIds = new Set(
        topics.map((t) => String(t.contentId)),
      )
      availableTopicsJS.forEach((opt) => {
        if (topicIds.has(String(opt.id))) {
          initTagIds.push(opt.id)
        }
      })
    } else {
      // Fallback: canonical centers failed to load — preserve the
      // profile's center content IDs directly so existing selections
      // aren't wiped.
      topics.forEach((t) => {
        if (t.contentId != null) initTagIds.push(Number(t.contentId))
      })
    }
  }

  const formState: SettingsFormState = {
    displayName: initDisplayName,
    bio: initBio,
    locationValue: initLocation,
    birthdayValue: initBirthday,
    selectedTagIds: initTagIds,
    profileVisibility: initProfileVis,
    locationVisibility: initLocationVis,
    birthdayVisibility: initBirthdayVis,
    memberSinceVisibility: initMemberSinceVis,
    bioVisibility: initBioVis,
    topicsVisibility: initTopicsVis,
    archetypesVisibility: initArchetypesVis,
    journeyStageVisibility: initJourneyStageVis,
    selectedArchetypes: initArchetypes,
    journeyStage: initJourney,
  }

  const initialValues: ProfileSettingsInitialValues = {
    displayName: initDisplayName,
    bio: initBio,
    location: initLocation,
    birthday: initBirthday,
    profileVisibility: initProfileVis,
    locationVisibility: initLocationVis,
    birthdayVisibility: initBirthdayVis,
    memberSinceVisibility: initMemberSinceVis,
    bioVisibility: initBioVis,
    topicsVisibility: initTopicsVis,
    archetypesVisibility: initArchetypesVis,
    journeyStageVisibility: initJourneyStageVis,
    centerContentIds: initTagIds.slice().sort().join(','),
    archetype: initArchetypes.slice().sort().join(','),
    journeyStage: initJourney || '',
  }

  return { formState, initialValues, ready: true }
}

export function buildInitialFormState (
  args: BuildInitialFormStateArgs,
): BuildInitialFormStateResult {
  const {
    profile, profileDataPresent, user, availableTopicsJS, tagOptionsReady,
  } = args

  // New user: profile query finished but returned null — use form defaults
  // plus the Redux display name. Does not require tag options.
  if (!profile && profileDataPresent) {
    return newUserResult(user)
  }

  // Existing user: need both the profile and settled tag options so we
  // can match topic IDs against canonical centers.
  if (profile && tagOptionsReady) {
    return existingUserResult(profile, availableTopicsJS)
  }

  // Not enough signal yet — return defaults but flag not-ready so the
  // caller can gate rendering. In the split container this branch is
  // unreachable in practice because the outer gate blocks until both
  // queries have settled.
  return {
    ...newUserResult(user),
    ready: false,
  }
}
